import {
  ADDED_LATER_KEYS,
  ADMIN_AVATAR_CLASS,
  AVATAR_CLASSES,
  BLOCKED_REASON_KEYS,
  CHANGED_LATER_KEYS,
  PARENT_MISSING_KEYS,
  PRICE_TABLES,
  UNIQUE_CONFLICT_KEYS,
} from "./constants";
import type {
  ActorNameOf,
  AvatarClassOf,
  BlockedReason,
  BlockedReasonOf,
  IsCurrentUserActor,
} from "./types";

export const isCurrentUserActor: IsCurrentUserActor = (
  actor,
  currentUserEmail,
) => actor.kind === "user" && actor.email === currentUserEmail;

export const actorNameOf: ActorNameOf = (actor, adminLabel) =>
  actor.kind === "admin" ? adminLabel : actor.email;

// Why the button is disabled; null when the action is allowed.
export const blockedReasonOf: BlockedReasonOf = ({
  action,
  direction,
  currentUserEmail,
  formatTime,
  adminLabel,
}): BlockedReason | null => {
  const { revertibility } = action;
  if (revertibility.state === "revertible") return null;
  if (revertibility.state === "undone") {
    // The rows are already where the button would put them, by hand.
    return {
      key: direction === "undo" ? "alreadyUndone" : "alreadyRedone",
      params: {},
    };
  }
  if (revertibility.reason !== "conflicts") {
    return { key: BLOCKED_REASON_KEYS[revertibility.reason], params: {} };
  }
  const gainedDependants = revertibility.conflicts.some(
    (conflict) => conflict.reason === "row_has_new_dependants",
  );
  if (action.blockedBy !== null) {
    const { actor, createdAt } = action.blockedBy;
    const time = formatTime(createdAt);
    const keys = gainedDependants ? ADDED_LATER_KEYS : CHANGED_LATER_KEYS;
    // German needs its own sentence for "you"; it can't be dropped into {{actor}}.
    if (isCurrentUserActor(actor, currentUserEmail)) {
      return { key: keys.byYou, params: { time } };
    }
    return {
      key: keys.byOther,
      params: { actor: actorNameOf(actor, adminLabel), time },
    };
  }
  const uniqueConflict = revertibility.conflicts.find(
    (conflict) => conflict.reason === "unique_conflict",
  );
  if (uniqueConflict !== undefined) {
    return {
      key: UNIQUE_CONFLICT_KEYS[uniqueConflict.table] ?? "typeExistsAgain",
      params: {},
    };
  }
  const missingParent = revertibility.conflicts.find(
    (conflict) => conflict.reason === "parent_missing",
  );
  if (missingParent !== undefined) {
    // Prices hang off a unit setting; unless listed, every other tracked row hangs off a type.
    return {
      key:
        PARENT_MISSING_KEYS[missingParent.table] ??
        (PRICE_TABLES.has(missingParent.table)
          ? "settingMissing"
          : "typeMissing"),
      params: {},
    };
  }
  return {
    key: gainedDependants ? "addedLater" : "changedOutside",
    params: {},
  };
};

// Stable per person, so the same email always gets the same colour.
export const avatarClassOf: AvatarClassOf = (actor) => {
  if (actor.kind === "admin") return ADMIN_AVATAR_CLASS;
  let hash = 0;
  for (const char of actor.email) {
    hash = (hash * 31 + char.charCodeAt(0)) % 100003;
  }
  return AVATAR_CLASSES[hash % AVATAR_CLASSES.length];
};
